/**
 * Ledger Chain Verifier — the pure integrity engine behind the buyer-facing
 * "tamper-evident" proof point.
 *
 * The Decision Ledger (src/lib/db/activity.ts) seals every governed event
 * with a synchronous FNV-1a hash chain: each entry's `hash` digests its
 * canonical fields + the previous entry's `hash` (genesis prevHash = '').
 * This module RECOMPUTES that exact scheme over an abstract, index-ordered
 * view of the chain so a one-click UI (VerifyChainBadge) can demonstrate —
 * not merely claim — that nothing was edited, reordered, or spliced.
 *
 * Deliberately pure and dependency-free below the type import: no db reads,
 * no async, no re-invented seal semantics. `fnvHash` mirrors the shipped
 * digest byte-for-byte (same FNV-1a constants, same 'fnv1a:' prefix, same
 * canonical field order for activity-shaped payloads), so a verified chain
 * here is the SAME chain the store sealed.
 */

import type { ActivityEntry } from '../db/types';

/** One link in the abstract chain the verifier walks. */
export type LedgerEntry = {
  /** Position in the chain, oldest-first, starting at 0 (genesis). */
  index: number;
  /** The sealed digest stored on the entry. */
  hash: string;
  /** The previous entry's hash ('' for the genesis entry). */
  prevHash: string;
  /** The content the digest covers. */
  payload: unknown;
};

/** Outcome of walking the chain: intact, or the first broken link. */
export type VerifyResult = {
  ok: boolean;
  /** Number of entries examined. */
  count: number;
  /** Index of the first broken link, or null when the chain is intact. */
  brokenAt: number | null;
  /** What the verifier recomputed/expected at the break (null when ok or unknowable). */
  expectedHash: string | null;
  /** What the entry actually carried at the break (null when ok or unknowable). */
  actualHash: string | null;
};

// ---------------------------------------------------------------------------
// FNV-1a digest — mirrors src/lib/db/activity.ts exactly
// ---------------------------------------------------------------------------

/** Tiny pure-JS 32-bit FNV-1a (hex), identical to the shipped seal. */
function fnv1aHex(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    // 32-bit FNV prime multiply via shifts, keeping the math in uint32.
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return 'fnv1a:' + h.toString(16).padStart(8, '0');
}

/** The canonical fields the shipped Decision Ledger chain covers. */
type ActivityLikePayload = Pick<
  ActivityEntry,
  'at' | 'event' | 'source' | 'kind' | 'summary' | 'subjectId'
>;

/** True when a payload carries the shipped canonical activity fields. */
function isActivityLike(p: unknown): p is ActivityLikePayload {
  if (typeof p !== 'object' || p === null) return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.at === 'string' &&
    typeof o.event === 'string' &&
    typeof o.source === 'string' &&
    typeof o.summary === 'string'
  );
}

/**
 * Recompute the chain digest for one link. Pure and deterministic.
 *
 * Activity-shaped payloads use the EXACT canonical serialization the store
 * seals with — `JSON.stringify([at, event, source, kind ?? '', summary,
 * subjectId ?? '', prevHash])` — so hashes match the shipped entries.
 * Any other payload digests as `JSON.stringify([payload, prevHash])`.
 */
export function fnvHash(prevHash: string, payload: unknown): string {
  const canonical = isActivityLike(payload)
    ? JSON.stringify([
        payload.at,
        payload.event,
        payload.source,
        payload.kind ?? '',
        payload.summary,
        payload.subjectId ?? '',
        prevHash,
      ])
    : JSON.stringify([payload, prevHash]);
  return fnv1aHex(canonical);
}

// ---------------------------------------------------------------------------
// Chain walk
// ---------------------------------------------------------------------------

/**
 * Walk the chain oldest-first and confirm, per entry:
 *   1. indices are contiguous from 0 (a gap or out-of-order entry means a
 *      link was removed or spliced — reported as the first anomaly, with
 *      hashes null because there is no meaningful digest to compare);
 *   2. `prevHash` equals the prior entry's `hash` (genesis = '') — a splice
 *      or reorder breaks here (expected = the true prior hash, actual = the
 *      prevHash the entry claims);
 *   3. `hash` equals a fresh {@link fnvHash} of prevHash + payload — any
 *      edit to the payload breaks here (expected = recomputed, actual =
 *      stored).
 * Returns the FIRST broken link; an empty chain is honestly ok with count 0.
 */
export function verifyChain(entries: LedgerEntry[]): VerifyResult {
  const count = entries.length;
  let prevHash = '';
  let nextIndex = 0;

  for (const e of entries) {
    // 1. Index continuity — gaps and out-of-order entries are anomalies.
    if (e.index !== nextIndex) {
      return { ok: false, count, brokenAt: e.index, expectedHash: null, actualHash: null };
    }
    // 2. Link to predecessor.
    if (e.prevHash !== prevHash) {
      return { ok: false, count, brokenAt: e.index, expectedHash: prevHash, actualHash: e.prevHash };
    }
    // 3. Entry's own seal.
    const recomputed = fnvHash(e.prevHash, e.payload);
    if (e.hash !== recomputed) {
      return { ok: false, count, brokenAt: e.index, expectedHash: recomputed, actualHash: e.hash };
    }
    prevHash = e.hash;
    nextIndex = e.index + 1;
  }

  return { ok: true, count, brokenAt: null, expectedHash: null, actualHash: null };
}

// ---------------------------------------------------------------------------
// Adapter: shipped ActivityEntry rows → abstract chain
// ---------------------------------------------------------------------------

/**
 * Shape sealed Decision Ledger rows (any order) into the oldest-first,
 * 0-indexed chain {@link verifyChain} walks. Legacy pre-chain rows (no hash)
 * are skipped, exactly as the store's own chain walk skips them. Pure —
 * callers (the badge's container) do the reading.
 */
export function toLedgerEntries(rows: ActivityEntry[]): LedgerEntry[] {
  return rows
    .filter((r) => typeof r.hash === 'string' && r.hash !== '')
    .sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0))
    .map((r, index) => ({
      index,
      hash: r.hash as string,
      prevHash: r.prevHash ?? '',
      payload: {
        at: r.at,
        event: r.event,
        source: r.source,
        kind: r.kind,
        summary: r.summary,
        subjectId: r.subjectId,
      },
    }));
}
